// Customer-facing testimonial submission page via invite link
// Uses MUI, styled-components, and matches admin testimonial fields (no approval switch)
// Reads token from URL, submits to /testimonials/submit-invite
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Typography,
    TextField,
    Button,
    Box,
    Rating,
    CircularProgress,
    Avatar,
    Alert,
    Grid,
} from '@mui/material';
import {
    PageContainer,
    FormContainer,
    MessageBox,
    StyledTextField,
} from './SubmitTestimonialPage.styles';
import axios from 'axios';

const backendUrl =
    import.meta.env.MODE === 'development'
        ? `http://${import.meta.env.VITE_BACKEND_URL}`
        : `https://${import.meta.env.VITE_BACKEND_URL_PROD}`;

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const SubmitTestimonialPage = () => {
    const query = useQuery();
    const token = query.get('token');

    const [form, setForm] = useState({
        name: '',
        location: '',
        rating: 5,
        date: new Date().toISOString().split('T')[0],
        content: '',
        avatarUrl: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setError('');
    };

    const handleRatingChange = (value) => {
        setForm((prev) => ({ ...prev, rating: value }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await axios.post(`${backendUrl}/testimonials/submit-invite`, {
                token,
                testimonial: form,
            });
            setSuccess(true);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    'Failed to submit testimonial. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <PageContainer>
                <FormContainer>
                    <MessageBox bgcolor="#fff3cd" color="#856404">
                        Invalid or missing invite link.
                    </MessageBox>
                </FormContainer>
            </PageContainer>
        );
    }

    if (success) {
        return (
            <PageContainer>
                <FormContainer>
                    <MessageBox bgcolor="#d4edda" color="#155724">
                        Thank you for your review! Your testimonial has been
                        submitted for approval.
                    </MessageBox>
                </FormContainer>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <FormContainer>
                <Typography variant="h4" align="center" gutterBottom>
                    Submit Your Testimonial
                </Typography>
                <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                    We appreciate your feedback! Please fill out the form below.
                </Typography>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                margin="normal"
                                label="Name"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                margin="normal"
                                label="Location"
                                name="location"
                                value={form.location}
                                onChange={handleChange}
                                required
                                placeholder="City, State"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ mt: 2 }}>
                                <Typography component="legend">
                                    Rating
                                </Typography>
                                <Rating
                                    name="rating"
                                    value={form.rating}
                                    onChange={(event, newValue) =>
                                        handleRatingChange(newValue)
                                    }
                                    precision={0.5}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                margin="normal"
                                label="Date"
                                name="date"
                                type="date"
                                value={form.date}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <StyledTextField
                                fullWidth
                                margin="normal"
                                label="Content"
                                name="content"
                                multiline
                                rows={4}
                                value={form.content}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                    </Grid>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 3 }}
                        disabled={loading}
                    >
                        {loading ? (
                            <CircularProgress size={24} />
                        ) : (
                            'Submit Testimonial'
                        )}
                    </Button>
                </Box>
            </FormContainer>
        </PageContainer>
    );
};

export default SubmitTestimonialPage;
